import FilledButtonM from "@common/components/button/FilledButtonM"
import IconButtonM from "@common/components/button/IconButtonM"
import { TabSelect } from "@common/components/select/TabSelect"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { DimensionsContext } from "@common/utils/mobileUtils"
import { useSystemBarInsets } from "@common/systemBars/SystemBarsFrame"
import {
    SUMMER_PRICES,
    SUMMER_RESUME_COMMITMENT,
    SUMMER_RESUME_FREQUENCIES,
    SUMMER_RESUME_TYPE,
    SUMMER_YEARLY_COMMITMENT_DISCOUNT,
    getSummerConfigSafe,
    getSummerResumeTypeDescription,
    getSummerResumeTypeLabel,
    isResumeOfferEngagementEligible,
    meetsRenewalDiscountFrequency,
    shouldOfferRenewalDiscount,
} from "@common/utils/summer2026"
import { ChangePlanBottomModalSection as Section } from "../../../../new-subscription/v2/components/ChangePlanBottomModalSection"
import {
    getOfferPrice,
    getOfferPricing,
} from "../../../../new-subscription/v2/newSubscriptionUtils"
import moment from "moment"
import React, {
    useContext,
    useEffect,
    useImperativeHandle,
    useMemo,
    useState,
} from "react"
import { DatePickerModal } from "react-native-paper-dates"
import { Portal } from "react-native-paper"
import {
    KeyboardAvoidingView,
    Modal,
    Platform,
    ScrollView,
    StyleSheet,
    TouchableOpacity,
    View,
} from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { connect } from "react-redux"

const ISO = "YYYY-MM-DD"
const COMMITMENT_HALF = SUMMER_YEARLY_COMMITMENT_DISCOUNT / 2

const HeroBadge = ({ label, color = theme.colors.summerDeep }) => (
    <View style={[styles.heroBadge, { backgroundColor: color }]}>
        <FontAwesome5 name={"star"} solid size={9} color={theme.colors.white} />
        <TextM fontSize={9} fontWeight={"Bold"} color={theme.colors.white}>
            {label}
        </TextM>
    </View>
)

// Badge signalant une option réservée à l'admin (visible uniquement quand un
// admin agit en tant que le parent via l'impersonation BO). Reprend le style
// `heroBadge` mais en warning + icône bouclier, comme le bandeau impersonation.
const AdminOnlyBadge = () => (
    <View
        style={[
            styles.heroBadge,
            { backgroundColor: theme.colors.warning ?? "#F0AD4E" },
        ]}
    >
        <FontAwesome5
            name={"user-shield"}
            solid
            size={9}
            color={theme.colors.white}
        />
        <TextM fontSize={9} fontWeight={"Bold"} color={theme.colors.white}>
            {"Réservé admin"}
        </TextM>
    </View>
)

// Options statiques (labels via helpers purs, icônes hardcoded) — extraites
// au module-level pour éviter `useMemo([])` qui ré-évalue sans bénéfice.
const TYPE_OPTIONS = [
    {
        label: getSummerResumeTypeLabel(SUMMER_RESUME_TYPE.FLEX_SHARED),
        value: SUMMER_RESUME_TYPE.FLEX_SHARED,
        description: getSummerResumeTypeDescription(
            SUMMER_RESUME_TYPE.FLEX_SHARED
        ),
        topChild: (
            <FontAwesome5
                name={"users"}
                size={16}
                color={theme.colors.primary}
            />
        ),
        floatingBadge: <HeroBadge label={"Le plus choisi"} />,
    },
    {
        label: getSummerResumeTypeLabel(SUMMER_RESUME_TYPE.FLEX_INDIVIDUAL),
        value: SUMMER_RESUME_TYPE.FLEX_INDIVIDUAL,
        description: getSummerResumeTypeDescription(
            SUMMER_RESUME_TYPE.FLEX_INDIVIDUAL
        ),
        topChild: (
            <FontAwesome5
                name={"user-alt"}
                size={16}
                color={theme.colors.primary}
            />
        ),
    },
]

// Option « Autonomie » : retirée du picker pour les parents, mais réactivable
// quand un admin impersonne le parent depuis le BO — afin de (re)poser ce
// choix à la demande d'un client. Gardée hors de TYPE_OPTIONS pour qu'aucun
// parent ne la voie ; injectée à la volée selon le contexte d'impersonation.
const AUTONOMY_TYPE_OPTION = {
    label: getSummerResumeTypeLabel(SUMMER_RESUME_TYPE.AUTONOMY),
    value: SUMMER_RESUME_TYPE.AUTONOMY,
    description: getSummerResumeTypeDescription(SUMMER_RESUME_TYPE.AUTONOMY),
    topChild: (
        <FontAwesome5
            name={"user-graduate"}
            size={16}
            color={theme.colors.primary}
        />
    ),
    floatingBadge: <AdminOnlyBadge />,
}

const resumeTypeToOfferType = (type) =>
    type === SUMMER_RESUME_TYPE.FLEX_INDIVIDUAL ? "integral" : "essential"

// =====================================================================
// SummerResumeEditModal — calqué sur ChangePlanBottomModal, dédié à la
// configuration de la reprise au 1er-7 septembre. Comprend type d'offre,
// fréquence hebdo, matières, engagement (toggle 12 mois avec callout
// fractionné 50€+50€ — gated par éligibilité commitment_ends_at),
// date de reprise + rappel garanties. Soft validation : Valider toujours
// cliquable, missing fields highlight en rouge.
// =====================================================================
const SummerResumeEditModal = ({
    bottomSheetRef,
    initialResume,
    schoolSubjects,
    defaultSubjectIds,
    pricingTable,
    sessionsPerWeek,
    user,
    // Contexte d'impersonation (admin BO agissant comme le parent). Quand
    // présent, débloque l'option « Autonomie » (réservé admin) dans le picker.
    impersonation,
    // True quand le parent est venu par le fast track Cours d'été — retire
    // l'option de reprise en cours partagés du picker.
    hasSummerFastTrack,
    summerConfig,
    // Cockpit-projected post-confirm commit_ends_at. Pre-confirm, this is
    // `user.commitment_ends_at` shifted by +31 days per local pause month
    // (mirrors `EagerSummerPrimitiveService::createPause`). When set, the
    // modal labels use this in place of `user.commitment_ends_at` so the
    // engagement date the parent sees matches what backend will write when
    // they tap Confirmer.
    projectedCommitmentEndDate,
    // Cockpit-projected lock state — derived from
    // `projectedCommitmentEndDate > 31/08`. Overrides the modal's own
    // backend-only derivation so the lock UI tracks local pause edits.
    commitmentLockedYearlyOverride,
    onValidate,
    onClose,
}) => {
    const dimensions = useContext(DimensionsContext)
    const systemBarInsets = useSystemBarInsets()

    // Admin agissant en tant que ce parent (impersonation BO) — débloque
    // l'option « Autonomie » réservée admin dans le picker de reprise.
    const isImpersonating = !!impersonation

    const summerCfg = useMemo(
        () => getSummerConfigSafe(summerConfig),
        [summerConfig]
    )

    // Engagement / renewal eligibility prime sur la projection cockpit
    // quand elle est dispo : un parent dont l'engagement courant survivait
    // à 31/07 backend-side mais qui retire ses pauses draft retombe sous
    // le cut-off — il doit voir la EngagementCard et l'offre de renouvellement.
    const engagementEligible = useMemo(
        () =>
            isResumeOfferEngagementEligible(
                user,
                summerConfig,
                projectedCommitmentEndDate
            ),
        [user, summerConfig, projectedCommitmentEndDate]
    )

    // Renewal discount eligibility — `commitment === YEARLY_12M` to itself
    // is NOT sufficient to advertise the −50€/M1 + −50€/M2 discount: when
    // a parent already has an active engagement past the rentrée (Testeur
    // case, commitment_ends_at in 2027), the modal forces commitment to
    // YEARLY_12M for read-only display, but no renewal is happening — the
    // discount must NOT be shown. Single source of truth: the helper below.
    const renewalDiscountEligible = useMemo(
        () =>
            shouldOfferRenewalDiscount(
                user,
                summerConfig,
                projectedCommitmentEndDate
            ),
        [user, summerConfig, projectedCommitmentEndDate]
    )

    // Parent already engaged past summer end (commitment_ends_at > 31/08).
    // The Rentrée picker must mirror this: commitment forced to YEARLY_12M
    // (read-only) and the toggle/CTA gets replaced by an info line that
    // surfaces the existing commitment date. The cockpit owns the lock
    // computation (it accounts for local draft pauses via
    // `projectCommitmentEndDate`) and passes it down as
    // `commitmentLockedYearlyOverride`. Defaults to `false` for safety if
    // a future non-cockpit consumer forgets to pass it.
    const commitmentLockedYearly = !!commitmentLockedYearlyOverride

    // Re-derived whenever the props feeding it change (modal stays mounted in
    // the Cockpit, so a stale [] dep would freeze defaults to the very first
    // open). The downstream useEffect mirrors this object into local state.
    const defaultsRef = useMemo(
        () => ({
            // L'offre Autonomie n'est plus proposée à la reprise rentrée pour
            // un parent : un type hydraté "autonomy" est neutralisé pour forcer
            // un choix d'offre courante. En impersonation (admin), on la
            // conserve — l'admin peut la (re)poser pour ce parent. La valeur
            // backend reste intacte tant qu'on ne re-soumet pas.
            type:
                !isImpersonating &&
                initialResume?.type === SUMMER_RESUME_TYPE.AUTONOMY
                    ? null
                    : (initialResume?.type ?? null),
            frequency: initialResume?.frequency ?? null,
            commitment: commitmentLockedYearly
                ? SUMMER_RESUME_COMMITMENT.YEARLY_12M
                : (initialResume?.commitment ??
                  SUMMER_RESUME_COMMITMENT.MONTHLY),
            resumeDate: initialResume?.resumeDate ?? summerCfg.resumeDate,
            subjectIds:
                initialResume?.subjectIds && initialResume.subjectIds.length > 0
                    ? initialResume.subjectIds
                    : defaultSubjectIds && defaultSubjectIds.length > 0
                      ? defaultSubjectIds
                      : [],
        }),
        [
            initialResume,
            defaultSubjectIds,
            commitmentLockedYearly,
            summerCfg,
            isImpersonating,
        ]
    )

    const [type, setType] = useState(defaultsRef.type)
    const [frequency, setFrequency] = useState(defaultsRef.frequency)
    const [commitment, setCommitment] = useState(defaultsRef.commitment)
    const [resumeDate, setResumeDate] = useState(defaultsRef.resumeDate)
    const [subjectIds, setSubjectIds] = useState(defaultsRef.subjectIds)
    const [datePickerVisible, setDatePickerVisible] = useState(false)
    const [submitAttempted, setSubmitAttempted] = useState(false)

    useEffect(() => {
        setType(defaultsRef.type)
        setFrequency(defaultsRef.frequency)
        setCommitment(defaultsRef.commitment)
        setResumeDate(defaultsRef.resumeDate)
        setSubjectIds(defaultsRef.subjectIds)
        setSubmitAttempted(false)
    }, [defaultsRef])

    // Locked-yearly path : si l'engagement actuel couvre déjà la rentrée,
    // on garde commitment = YEARLY_12M en permanence (read-only). Sinon,
    // si l'utilisateur n'est pas éligible au new-deal 12 mois et que
    // l'état initial est en YEARLY_12M (legacy), on rétrograde en mensuel.
    useEffect(() => {
        if (commitmentLockedYearly) {
            if (commitment !== SUMMER_RESUME_COMMITMENT.YEARLY_12M) {
                setCommitment(SUMMER_RESUME_COMMITMENT.YEARLY_12M)
            }
            return
        }
        if (
            !engagementEligible &&
            commitment === SUMMER_RESUME_COMMITMENT.YEARLY_12M
        ) {
            setCommitment(SUMMER_RESUME_COMMITMENT.MONTHLY)
        }
    }, [commitmentLockedYearly, engagementEligible, commitment])

    // Reset frequency si elle n'est plus dans le catalogue du type courant
    // (ex: passage de Particuliers 1.5h → Partagés où 1.5h n'existe pas).
    useEffect(() => {
        if (!frequency || !type) return
        const allowed =
            type === SUMMER_RESUME_TYPE.FLEX_SHARED
                ? SUMMER_RESUME_FREQUENCIES.flex_shared
                : type === SUMMER_RESUME_TYPE.FLEX_INDIVIDUAL
                  ? SUMMER_RESUME_FREQUENCIES.flex_individual
                  : []
        if (allowed.length > 0 && !allowed.includes(frequency)) {
            setFrequency(null)
        }
    }, [type, frequency])

    const needsFrequencyAndSubjects =
        type === SUMMER_RESUME_TYPE.FLEX_SHARED ||
        type === SUMMER_RESUME_TYPE.FLEX_INDIVIDUAL

    const subjectOptions = useMemo(
        () =>
            (schoolSubjects || []).map((s) => ({
                label: s.name,
                value: s.id,
                labelIconImage: s.icon,
            })),
        [schoolSubjects]
    )

    // Picker d'offres : le parent ne voit que Cours partagés / particuliers.
    // L'admin en impersonation voit en plus « Autonomie » (réservé admin),
    // pour la reposer à la demande d'un client.
    // Parent venu par le fast track Cours d'été ("new user") : la reprise en
    // cours partagés (FLEX_SHARED) lui est retirée du picker — mirror du gate
    // backend (SummerChoiceController rejette `resume_offer_type = shared`).
    // Le cockpit est déjà masqué pour lui en amont ; ce filtre est une défense
    // en profondeur si la modale est atteinte (état périmé / lien profond).
    const typeOptions = useMemo(() => {
        const base = isImpersonating
            ? [...TYPE_OPTIONS, AUTONOMY_TYPE_OPTION]
            : TYPE_OPTIONS
        return hasSummerFastTrack
            ? base.filter((o) => o.value !== SUMMER_RESUME_TYPE.FLEX_SHARED)
            : base
    }, [isImpersonating, hasSummerFastTrack])

    const offerPricing = useMemo(() => {
        if (!needsFrequencyAndSubjects || !pricingTable) return null
        return getOfferPricing({
            pricingTable,
            offerType: resumeTypeToOfferType(type),
            sessionsPerWeek,
            withCommitment: commitment === SUMMER_RESUME_COMMITMENT.YEARLY_12M,
        })
    }, [
        needsFrequencyAndSubjects,
        pricingTable,
        sessionsPerWeek,
        type,
        commitment,
    ])

    // Fréquences proposées = single source of truth = `offerPricing` dérivé
    // de `pricingTableV2` (même catalogue que /subscribe/create-subscription).
    // Fallback statique `SUMMER_RESUME_FREQUENCIES` si le pricing n'est pas
    // encore chargé. Pour Cours partagés : 1h-5h. Pour Particuliers : ajout
    // de 1.5h. La valeur 6h n'existe plus dans le catalogue Stripe.
    const frequencyOptions = useMemo(() => {
        if (offerPricing) {
            return Object.values(offerPricing)
                .filter((p) => typeof p.weeklyHours === "number")
                .sort((a, b) => a.weeklyHours - b.weeklyHours)
                .map((p) => ({
                    label: `${p.weeklyHours}h`,
                    value: p.weeklyHours,
                }))
        }
        if (!needsFrequencyAndSubjects) return []
        const fallback = SUMMER_RESUME_FREQUENCIES[type] || []
        return fallback.map((h) => ({ label: `${h}h`, value: h }))
    }, [offerPricing, needsFrequencyAndSubjects, type])

    const pricing = useMemo(() => {
        if (!offerPricing || !frequency) return null
        if (!offerPricing[frequency]) return null
        return getOfferPrice({ offerPricing, hoursPerWeek: frequency })
    }, [offerPricing, frequency])

    const isTypeMissing = !type
    const isFrequencyMissing = needsFrequencyAndSubjects && !frequency
    const isSubjectsMissing =
        needsFrequencyAndSubjects && subjectIds.length === 0
    const isDateMissing = !resumeDate
    const hasErrors =
        isTypeMissing ||
        isFrequencyMissing ||
        isSubjectsMissing ||
        isDateMissing

    const handleSelectType = (next) => {
        setType(next)
    }

    const handleSelectFrequency = (next) => {
        setFrequency(next)
        setSubjectIds((prev) => prev.slice(0, next))
    }

    const handleValidate = () => {
        if (hasErrors) {
            setSubmitAttempted(true)
            return
        }
        onValidate?.({
            type,
            frequency: needsFrequencyAndSubjects ? frequency : null,
            commitment,
            resumeDate,
            subjectIds: needsFrequencyAndSubjects ? subjectIds : [],
        })
        onClose?.()
    }

    const showError = (flag) => submitAttempted && flag

    // Prefer the cockpit-projected commit (includes the effect of draft
    // pauses) over the raw backend value so the modal label tracks what
    // the parent will see post-confirm. Falls back to `user.commitment_ends_at`
    // when no projection is provided (e.g. modal opened outside the cockpit).
    const displayCommitmentEndDate =
        projectedCommitmentEndDate ?? user?.commitment_ends_at

    // "Projected" = the parent's local pause drafts shift commit forward,
    // i.e. the displayed date is what they WILL see after confirming, not
    // the current backend value. Drives the copy ("se terminera" vs.
    // "se termine") so the user knows whether the date is a projection.
    const commitmentEndsAtIsProjected =
        !!projectedCommitmentEndDate &&
        !!user?.commitment_ends_at &&
        !moment(projectedCommitmentEndDate).isSame(
            moment(user.commitment_ends_at),
            "day"
        )

    const commitmentEndsAtLabel =
        displayCommitmentEndDate && engagementEligible
            ? moment(displayCommitmentEndDate).format("D MMMM YYYY")
            : null

    // Date de fin d'engagement courant pour le cas locked-yearly (parent
    // déjà engagé au-delà de la fenêtre été — affichage read-only au
    // lieu du toggle + new-deal CTA).
    const lockedCommitmentEndsAtLabel =
        commitmentLockedYearly && displayCommitmentEndDate
            ? moment(displayCommitmentEndDate).format("D MMMM YYYY")
            : null

    const [visible, setVisible] = useState(false)
    useImperativeHandle(
        bottomSheetRef,
        () => ({
            present: () => setVisible(true),
            dismiss: () => setVisible(false),
        }),
        []
    )

    const isLargeScreen = (dimensions?.width ?? 0) > 800

    const handleRequestClose = () => {
        setVisible(false)
        onClose?.()
    }

    const headerBlock = (
        <View style={styles.header}>
            <View style={{ flex: 1, alignItems: "center" }}>
                <TextM fontSize={20} fontWeight={"Bold"} center>
                    Votre rentrée
                </TextM>
                <TextM
                    fontSize={12}
                    color={theme.colors.grey}
                    center
                    style={{ marginTop: 4 }}
                >
                    Reprise entre le 1er et le 7 septembre
                </TextM>
            </View>
            <IconButtonM
                icon={"times"}
                size={22}
                iconColor={theme.colors.primary}
                style={styles.closeButton}
                onPress={handleRequestClose}
            />
        </View>
    )

    const content = (
        <KeyboardAvoidingView
            behavior={Platform.OS === "ios" ? "padding" : "height"}
            style={{
                flex: isLargeScreen ? undefined : 1,
                backgroundColor: theme.colors.white,
                borderRadius: isLargeScreen ? 12 : 0,
                height: isLargeScreen ? "90%" : undefined,
                width: isLargeScreen
                    ? Math.min(640, dimensions?.width ?? 640)
                    : undefined,
                overflow: "hidden",
            }}
        >
            {headerBlock}
            <ScrollView contentContainerStyle={styles.content}>
                <SectionWrap
                    icon={"play-circle"}
                    title={"Quelle offre ?"}
                    error={showError(isTypeMissing)}
                    errorText={"Choisissez une offre"}
                >
                    <TabSelect
                        options={typeOptions}
                        selected={type}
                        onSelect={handleSelectType}
                        cardStyle={{
                            flex: 1,
                            minWidth: dimensions?.width > 800 ? 220 : 200,
                        }}
                    />
                </SectionWrap>

                {needsFrequencyAndSubjects ? (
                    <SectionWrap
                        icon={"clock"}
                        title={"Heures par semaine"}
                        error={showError(isFrequencyMissing)}
                        errorText={"Choisissez un nombre d'heures"}
                    >
                        <TabSelect
                            options={frequencyOptions}
                            selected={frequency}
                            onSelect={handleSelectFrequency}
                            cardStyle={{
                                minWidth: 80,
                                alignItems: "center",
                            }}
                        />
                    </SectionWrap>
                ) : null}

                {needsFrequencyAndSubjects ? (
                    <SectionWrap
                        icon={"book"}
                        title={"Matières souhaitées"}
                        error={showError(isSubjectsMissing)}
                        errorText={"Choisissez au moins une matière"}
                        helper={
                            frequency
                                ? `Jusqu'à ${frequency} matière${
                                      frequency > 1 ? "s" : ""
                                  }`
                                : "Sélectionnez d'abord vos heures hebdo"
                        }
                    >
                        <TabSelect
                            multiple
                            options={subjectOptions}
                            selected={subjectIds}
                            onSelect={setSubjectIds}
                            maxSelection={frequency || null}
                            cardStyle={{ borderRadius: 30 }}
                        />
                    </SectionWrap>
                ) : null}

                {commitmentLockedYearly ? (
                    <SectionWrap icon={"handshake"} title={"Engagement"}>
                        <View style={styles.lockedCommitmentCard}>
                            <FontAwesome5
                                name={"lock"}
                                solid
                                size={14}
                                color={theme.colors.primary}
                                style={{ marginRight: 10 }}
                            />
                            <View style={{ flex: 1 }}>
                                <TextM fontWeight={"Bold"} fontSize={14} wrap>
                                    Engagement en cours
                                </TextM>
                                {lockedCommitmentEndsAtLabel ? (
                                    <TextM
                                        fontSize={12}
                                        color={theme.colors.grey}
                                        wrap
                                        style={{ marginTop: 2 }}
                                    >
                                        {commitmentEndsAtIsProjected
                                            ? `Avec vos pauses d'été, votre engagement se terminera le ${lockedCommitmentEndsAtLabel} (au lieu du ${moment(user.commitment_ends_at).format("D MMMM YYYY")}).`
                                            : `Votre engagement actuel se termine le ${lockedCommitmentEndsAtLabel}.`}
                                    </TextM>
                                ) : null}
                            </View>
                        </View>
                    </SectionWrap>
                ) : engagementEligible ? (
                    <SectionWrap icon={"handshake"} title={"Engagement"}>
                        <EngagementCard
                            active={
                                commitment ===
                                SUMMER_RESUME_COMMITMENT.YEARLY_12M
                            }
                            onPress={() =>
                                setCommitment(
                                    commitment ===
                                        SUMMER_RESUME_COMMITMENT.YEARLY_12M
                                        ? SUMMER_RESUME_COMMITMENT.MONTHLY
                                        : SUMMER_RESUME_COMMITMENT.YEARLY_12M
                                )
                            }
                            commitmentEndsAtLabel={commitmentEndsAtLabel}
                            commitmentEndsAtIsProjected={
                                commitmentEndsAtIsProjected
                            }
                            currentCommitmentEndsAtLabel={
                                user?.commitment_ends_at
                                    ? moment(user.commitment_ends_at).format(
                                          "D MMMM YYYY"
                                      )
                                    : null
                            }
                            showDiscount={
                                renewalDiscountEligible &&
                                meetsRenewalDiscountFrequency(frequency)
                            }
                        />
                    </SectionWrap>
                ) : null}

                <SectionWrap
                    icon={"calendar-alt"}
                    title={"Date de reprise"}
                    error={showError(isDateMissing)}
                    errorText={"Choisissez une date entre le 1er et le 7 sept."}
                    helper={
                        "Du 1er septembre à votre date de reprise, l'abonnement reste en pause."
                    }
                >
                    <TouchableOpacity
                        onPress={() => setDatePickerVisible(true)}
                        activeOpacity={0.85}
                        style={styles.datePickerButton}
                    >
                        <TextM fontWeight={"Medium"}>
                            {moment(resumeDate).format("D MMMM YYYY")}
                        </TextM>
                        <FontAwesome5
                            name={"chevron-down"}
                            size={12}
                            color={theme.colors.grey}
                        />
                    </TouchableOpacity>
                </SectionWrap>

                <View style={styles.guaranteesBox}>
                    <TextM
                        fontWeight={"Bold"}
                        fontSize={12}
                        color={theme.colors.grey}
                        uppercase
                        style={{ letterSpacing: 0.4 }}
                    >
                        Garanties Masteur
                    </TextM>
                    <GuaranteeRow
                        icon={"pause-circle"}
                        label={"Pauses vacances scolaires sans frais"}
                    />
                    <GuaranteeRow
                        icon={"user-friends"}
                        label={"Changement de tuteur à tout moment"}
                    />
                    <GuaranteeRow
                        icon={"sliders-h"}
                        label={"Heures flexibles d'une semaine à l'autre"}
                    />
                </View>
            </ScrollView>

            <View style={styles.footer}>
                {pricing ? (
                    <View style={styles.priceCard}>
                        <View style={{ flex: 1 }}>
                            <TextM
                                fontSize={12}
                                color={theme.colors.light}
                                fontWeight={"Medium"}
                            >
                                {pricing.hoursPerMonth} h / mois
                            </TextM>
                            <View style={styles.priceRow}>
                                <TextM
                                    fontWeight={"Bold"}
                                    fontSize={22}
                                    color={theme.colors.primary}
                                >
                                    {pricing.monthlyPrice}€
                                </TextM>
                                <TextM
                                    fontSize={12}
                                    color={theme.colors.grey}
                                    style={{ marginLeft: 4 }}
                                >
                                    / mois
                                </TextM>
                            </View>
                            {commitment ===
                                SUMMER_RESUME_COMMITMENT.YEARLY_12M &&
                            renewalDiscountEligible &&
                            meetsRenewalDiscountFrequency(frequency) ? (
                                <TextM
                                    fontSize={11}
                                    color={theme.colors.success}
                                    style={{ marginTop: 2 }}
                                >
                                    − {COMMITMENT_HALF}€ au 1er mois et −{" "}
                                    {COMMITMENT_HALF}€ au 2e mois
                                </TextM>
                            ) : null}
                        </View>
                        <TextM
                            fontWeight={"Bold"}
                            fontSize={16}
                            color={theme.colors.primary}
                        >
                            {Math.round(pricing.hourlyPrice)}€/h
                        </TextM>
                    </View>
                ) : type === SUMMER_RESUME_TYPE.AUTONOMY ? (
                    <View style={styles.priceCard}>
                        <TextM fontWeight={"Bold"} fontSize={16}>
                            {SUMMER_PRICES.AUTONOMY_MONTHLY_PRICE}€ / mois
                        </TextM>
                    </View>
                ) : null}

                <FilledButtonM
                    label={"Choisir"}
                    onPress={handleValidate}
                    style={{ width: "100%", marginBottom: 0 }}
                />
            </View>
        </KeyboardAvoidingView>
    )

    const datePicker = (
        <DatePickerModal
            locale={"fr"}
            mode={"single"}
            startWeekOnMonday
            visible={datePickerVisible}
            onDismiss={() => setDatePickerVisible(false)}
            date={new Date(resumeDate)}
            onConfirm={({ date }) => {
                setDatePickerVisible(false)
                if (date) setResumeDate(moment(date).format(ISO))
            }}
            validRange={{
                // react-native-paper-dates applique `setUTCHours(...)` à
                // validRange.{start,end}Date AVANT de comparer aux cellules
                // calendrier (créées en heure locale: `new Date(y, m, d)`).
                // Cette UTC-isation décale la borne dans les fuseaux >0
                // (ex: CEST +02:00) et fait apparaître le J+1 comme valide.
                // On construit donc les bornes via les composants locaux
                // (year/month/day) pour neutraliser l'écart de fuseau.
                startDate: moment(summerCfg.resumeDate).startOf("day").toDate(),
                endDate: moment(summerCfg.resumeEnd).startOf("day").toDate(),
            }}
        />
    )

    if (isLargeScreen) {
        return (
            <Portal>
                <Modal
                    visible={visible}
                    transparent
                    animationType={"fade"}
                    onRequestClose={handleRequestClose}
                >
                    <View
                        style={{
                            flex: 1,
                            backgroundColor: theme.colors.blackTransparent2,
                            justifyContent: "center",
                            alignItems: "center",
                        }}
                    >
                        <TouchableOpacity
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                            }}
                            activeOpacity={1}
                            onPress={handleRequestClose}
                        />
                        {content}
                    </View>
                    {datePicker}
                </Modal>
            </Portal>
        )
    }

    return (
        <Modal
            visible={visible}
            transparent
            animationType={"slide"}
            onRequestClose={handleRequestClose}
        >
            <View
                style={{
                    flex: 1,
                    backgroundColor: theme.colors.blackTransparent2,
                    justifyContent: "flex-end",
                }}
            >
                <View
                    style={{
                        height: "90%",
                        backgroundColor: theme.colors.white,
                        borderTopLeftRadius: 16,
                        borderTopRightRadius: 16,
                        overflow: "hidden",
                        // A modal is a window of its own, drawn behind
                        // Android's navigation buttons.
                        paddingBottom: systemBarInsets.bottom,
                    }}
                >
                    {content}
                </View>
            </View>
            {datePicker}
        </Modal>
    )
}

const SectionWrap = ({ icon, title, error, errorText, helper, children }) => (
    <View style={[styles.sectionWrap, error && styles.sectionWrapError]}>
        <Section icon={icon} title={title}>
            {children}
        </Section>
        {error ? (
            <TextM
                fontSize={11}
                color={theme.colors.error}
                wrap
                style={{ marginTop: 4 }}
            >
                {errorText}
            </TextM>
        ) : helper ? (
            <TextM
                fontSize={11}
                color={theme.colors.light}
                wrap
                style={{ marginTop: 4 }}
            >
                {helper}
            </TextM>
        ) : null}
    </View>
)

const EngagementCard = ({
    active,
    onPress,
    commitmentEndsAtLabel,
    commitmentEndsAtIsProjected = false,
    currentCommitmentEndsAtLabel = null,
    showDiscount = false,
}) => (
    <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.85}
        style={[styles.engagementCard, active && styles.engagementCardActive]}
    >
        <View
            style={[
                styles.engagementCheck,
                active && styles.engagementCheckActive,
            ]}
        >
            {active ? (
                <FontAwesome5
                    name={"check"}
                    solid
                    size={11}
                    color={theme.colors.white}
                />
            ) : null}
        </View>
        <View style={{ flex: 1 }}>
            <View
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 6,
                }}
            >
                <TextM fontWeight={"Bold"} fontSize={14}>
                    Engagement 12 mois
                </TextM>
                {showDiscount ? (
                    <View style={styles.giftPill}>
                        <FontAwesome5
                            name={"gift"}
                            solid
                            size={9}
                            color={theme.colors.white}
                            style={{ marginRight: 4 }}
                        />
                        <TextM
                            fontWeight={"ExtraBold"}
                            fontSize={10}
                            color={theme.colors.white}
                        >
                            {SUMMER_YEARLY_COMMITMENT_DISCOUNT}€ offerts
                        </TextM>
                    </View>
                ) : null}
            </View>
            {showDiscount ? (
                <TextM
                    fontSize={12}
                    color={theme.colors.text}
                    wrap
                    style={{ marginTop: 4 }}
                >
                    {COMMITMENT_HALF}€ offerts au 1er mois{" "}
                    <TextM fontSize={12} color={theme.colors.grey}>
                        puis
                    </TextM>{" "}
                    {COMMITMENT_HALF}€ au 2e mois.
                </TextM>
            ) : null}
            {commitmentEndsAtLabel ? (
                <TextM
                    fontSize={11}
                    color={theme.colors.light}
                    wrap
                    style={{ marginTop: 4 }}
                >
                    {commitmentEndsAtIsProjected && currentCommitmentEndsAtLabel
                        ? `Avec vos pauses d'été, votre engagement se terminera le ${commitmentEndsAtLabel} (au lieu du ${currentCommitmentEndsAtLabel}).`
                        : `Votre engagement actuel se termine le ${commitmentEndsAtLabel}.`}
                </TextM>
            ) : null}
        </View>
    </TouchableOpacity>
)

const GuaranteeRow = ({ icon, label }) => (
    <View style={styles.guaranteeRow}>
        <FontAwesome5
            name={icon}
            solid
            size={11}
            color={theme.colors.success}
            style={{ width: 18 }}
        />
        <TextM
            fontSize={12}
            color={theme.colors.text}
            wrap
            style={{ flex: 1, marginLeft: 4 }}
        >
            {label}
        </TextM>
    </View>
)

const styles = StyleSheet.create({
    header: {
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.lightMore,
        flexDirection: "row",
        alignItems: "center",
    },
    closeButton: {
        position: "absolute",
        right: 8,
        top: 10,
    },
    content: {
        padding: 16,
        gap: 20,
    },
    footer: {
        padding: 16,
        borderTopWidth: 1,
        borderTopColor: theme.colors.lightMore,
        gap: 12,
    },
    priceCard: {
        backgroundColor: theme.colors.lightMoreTransparent,
        padding: 16,
        borderRadius: 10,
        flexDirection: "row",
        alignItems: "center",
    },
    priceRow: {
        flexDirection: "row",
        alignItems: "baseline",
        marginTop: 4,
    },
    sectionWrap: {
        gap: 4,
    },
    sectionWrapError: {
        borderLeftWidth: 3,
        borderLeftColor: theme.colors.error,
        paddingLeft: 10,
    },
    engagementCard: {
        flexDirection: "row",
        alignItems: "center",
        padding: 14,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.white,
    },
    lockedCommitmentCard: {
        flexDirection: "row",
        alignItems: "flex-start",
        padding: 14,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: theme.colors.lightMore,
        backgroundColor: theme.colors.lightMoreTransparent,
    },
    engagementCardActive: {
        borderColor: theme.colors.success,
        borderWidth: 2,
        backgroundColor:
            theme.colors.successLight || theme.colors.lightMoreTransparent,
    },
    engagementCheck: {
        width: 22,
        height: 22,
        borderRadius: 11,
        borderWidth: 2,
        borderColor: theme.colors.lightMore,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 12,
    },
    engagementCheckActive: {
        borderColor: theme.colors.success,
        backgroundColor: theme.colors.success,
    },
    giftPill: {
        flexDirection: "row",
        alignItems: "center",
        backgroundColor: theme.colors.success,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 999,
    },
    heroBadge: {
        paddingHorizontal: 10,
        paddingVertical: 3,
        borderRadius: 10,
        flexDirection: "row",
        alignItems: "center",
        gap: 4,
    },
    datePickerButton: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "space-between",
        backgroundColor: theme.colors.white,
        padding: 15,
        borderRadius: 15,
        borderWidth: 1,
        borderColor: theme.colors.light,
    },
    guaranteesBox: {
        backgroundColor: theme.colors.greyBg,
        borderRadius: 12,
        padding: 12,
        gap: 6,
    },
    guaranteeRow: {
        flexDirection: "row",
        alignItems: "center",
    },
})

const mapStateToProps = (state) => ({
    schoolSubjects: state.data.formData?.schoolSubjects,
    pricingTable: state.data.formData?.pricingTableV2,
    sessionsPerWeek: state.data.formData?.sessionsPerWeek,
    user: state.data.formData?.user,
    summerConfig: state.data.staticData?.summer_config,
    // Présent uniquement quand un admin agit en tant que ce parent (BO →
    // bootstrap impersonation). Débloque l'option « Autonomie » (réservé admin)
    // dans le picker de reprise rentrée.
    impersonation: state.data?.currentUser?.object?.impersonation ?? null,
    // Gate "new user" (fast track Cours d'été). Lu depuis currentUser.object,
    // qui porte les champs été du UserResource (formData.user ne les porte pas).
    hasSummerFastTrack: !!(
        state.data?.currentUser?.object?.has_summer_fast_track ??
        state.data?.formData?.user?.has_summer_fast_track
    ),
})

export default connect(mapStateToProps)(SummerResumeEditModal)
